import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface DiaRotasResumo {
    rotas: number;
    leads: number;
    leadsConcluidos: number;
    km: number;
    emExecucao: boolean;
    concluida: boolean; // todas as rotas do dia concluídas
    atrasada: boolean;  // dia passado com paradas pendentes
}

interface CalendarioRotasProps {
    mes: Date;
    onChangeMes: (d: Date) => void;
    dataSel: string; // YYYY-MM-DD
    onSelectDate: (d: string) => void;
    resumoPorDia: Record<string, DiaRotasResumo>;
    /** Chamado ao soltar um lead (drag nativo) numa data futura — remanejamento. */
    onDropDia?: (iso: string) => void;
    /** True enquanto um lead está sendo arrastado (destaca as datas que aceitam drop). */
    arrastando?: boolean;
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const statusDoDia = (r: DiaRotasResumo): { label: string; cls: string } => {
    if (r.atrasada) return { label: 'Atrasada', cls: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30' };
    if (r.emExecucao) return { label: 'Em andamento', cls: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' };
    if (r.concluida) return { label: 'Concluída', cls: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30' };
    return { label: 'Planejada', cls: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30' };
};

/**
 * Calendário mensal de rotas do operador de campo (Motoboy v3):
 * quantidade de rotas/leads, status e km por dia; células aceitam drop de leads.
 */
const CalendarioRotas: React.FC<CalendarioRotasProps> = ({ mes, onChangeMes, dataSel, onSelectDate, resumoPorDia, onDropDia, arrastando }) => {
    const hoje = isoDate(new Date());
    const [hoverDia, setHoverDia] = useState<string | null>(null);

    const semanas = useMemo(() => {
        const primeiro = new Date(mes.getFullYear(), mes.getMonth(), 1);
        const inicio = new Date(primeiro);
        inicio.setDate(inicio.getDate() - inicio.getDay());
        const dias: Date[] = [];
        const cursor = new Date(inicio);
        for (let i = 0; i < 42; i++) {
            dias.push(new Date(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }
        const rows: Date[][] = [];
        for (let i = 0; i < 6; i++) {
            const semana = dias.slice(i * 7, i * 7 + 7);
            if (i > 0 && semana.every(d => d.getMonth() !== mes.getMonth())) break;
            rows.push(semana);
        }
        return rows;
    }, [mes]);

    const mudarMes = (delta: number) => onChangeMes(new Date(mes.getFullYear(), mes.getMonth() + delta, 1));

    const irHoje = () => {
        const agora = new Date();
        onChangeMes(new Date(agora.getFullYear(), agora.getMonth(), 1));
        onSelectDate(isoDate(agora));
    };

    const celula = (d: Date) => {
        const iso = isoDate(d);
        const doMes = d.getMonth() === mes.getMonth();
        const resumo = resumoPorDia[iso];
        const selecionado = iso === dataSel;
        const ehHoje = iso === hoje;
        const status = resumo && resumo.rotas > 0 ? statusDoDia(resumo) : null;
        // Drop nativo: aceita soltar em datas de hoje em diante (não no passado)
        const passado = iso < hoje;
        const podeSoltar = !!onDropDia && !passado;
        const sobreEste = hoverDia === iso;
        const destacar = (arrastando && podeSoltar && !selecionado) || sobreEste;

        return (
            <div
                key={iso}
                onDragOver={podeSoltar ? (e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }) : undefined}
                onDragEnter={podeSoltar ? (() => setHoverDia(iso)) : undefined}
                onDragLeave={podeSoltar ? (() => setHoverDia(h => (h === iso ? null : h))) : undefined}
                onDrop={podeSoltar ? (e => { e.preventDefault(); setHoverDia(null); onDropDia!(iso); }) : undefined}
            >
                <button
                    onClick={() => onSelectDate(iso)}
                    className={`relative w-full min-h-[72px] sm:min-h-[84px] rounded-xl p-1.5 text-left border transition-all flex flex-col
                        ${selecionado
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                            : sobreEste
                                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 ring-2 ring-emerald-400/70'
                                : destacar
                                    ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-900/10'
                                    : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40'}
                        ${arrastando && passado ? 'opacity-25' : doMes ? '' : 'opacity-35'}`}
                >
                    <span className={`text-[11px] font-bold w-5 h-5 flex items-center justify-center rounded-full mb-0.5
                        ${ehHoje ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300'}`}>
                        {d.getDate()}
                    </span>
                    {resumo && resumo.rotas > 0 && (
                        <div className="space-y-0.5 overflow-hidden">
                            <span className="block text-[9px] font-bold text-gray-700 dark:text-gray-200 truncate">
                                🛵 {resumo.rotas} rota{resumo.rotas > 1 ? 's' : ''} · {resumo.leads} lead{resumo.leads !== 1 ? 's' : ''}
                            </span>
                            {status && (
                                <span className={`inline-block text-[8px] font-extrabold uppercase px-1 py-px rounded ${status.cls}`}>
                                    {status.label}
                                </span>
                            )}
                            {resumo.km > 0 && (
                                <span className="hidden sm:block text-[9px] font-semibold text-gray-400 truncate">~{Math.round(resumo.km)} km</span>
                            )}
                        </div>
                    )}
                </button>
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-1">
                    <button onClick={() => mudarMes(-1)} className="p-1.5 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Mês anterior">
                        <ChevronLeft size={18} />
                    </button>
                    <button onClick={() => mudarMes(1)} className="p-1.5 rounded-full text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Próximo mês">
                        <ChevronRight size={18} />
                    </button>
                    <button onClick={irHoje} className="ml-1 px-3 py-1.5 text-xs font-bold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                        Hoje
                    </button>
                </div>
                <p className="text-sm font-bold text-gray-800 dark:text-gray-100">{MESES[mes.getMonth()]} {mes.getFullYear()}</p>
            </div>

            <div className="grid grid-cols-7 mb-1">
                {DIAS_SEMANA.map(d => (
                    <p key={d} className="text-center text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest py-1">{d}</p>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {semanas.flat().map(d => celula(d))}
            </div>

            {onDropDia && (
                <p className="text-[10px] text-gray-400 mt-2">💡 Arraste um lead da lista do dia para outra data do calendário para remanejá-lo.</p>
            )}
        </div>
    );
};

export default CalendarioRotas;
